import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { auth } from '../../../../firebase';
import { onAuthStateChanged } from 'firebase/auth';

/**
 * Unified Responsive Navbar Component
 * - Desktop (>= 768px): Docked left sidebar (256px wide, sticky)
 * - Mobile (< 768px): Slide-over drawer with backdrop overlay
 */
const Navbar = ({ isMenuActive, toggleMenu }) => {
    const [menu, setMenu] = useState('dashboard');
    const [isAdmin, setIsAdmin] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const path = location.pathname;
        if (path === '/dashboard' || path === '/dashboard/') {
            setMenu('dashboard');
        } else if (path.includes('/dashboard/sales-analytics')) {
            setMenu('sales');
        } else if (path.includes('/dashboard/product-analytics')) {
            setMenu('product-analytics');
        } else if (path.includes('/dashboard/expense-management')) {
            setMenu('expenses');
        } else if (path.includes('/dashboard/website-analytics')) {
            setMenu('website-analytics');
        } else if (path.includes('/dashboard/userList')) {
            setMenu('userList');
        } else if (path.includes('/dashboard/collection')) {
            setMenu('collection');
        } else if (path.includes('/dashboard/product')) {
            setMenu('product');
        } else if (path.includes('/dashboard/banners')) {
            setMenu('banners');
        } else if (path.includes('/dashboard/order_list')) {
            setMenu('order');
        } else if (path.includes('/dashboard/gallery')) {
            setMenu('gallery');
        } else if (path.includes('/dashboard/customerGallery')) {
            setMenu('customerGallery');
        } else if (path.includes('/dashboard/featured_product')) {
            setMenu('featured product');
        } else if (path.includes('/dashboard/promo_code')) {
            setMenu('promo_code');
        }

        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
            if (currentUser) {
                try {
                    const token = await currentUser.getIdTokenResult();
                    setIsAdmin(token.claims?.role === 'admin');
                } catch (error) {
                    setIsAdmin(true); // Fallback to true in CMS layout
                }
            } else {
                setIsAdmin(true); // Default to show admin items in CMS view
            }
        });

        return () => unsubscribe();
    }, [location.pathname]);

    // Close menu when clicking a link on mobile
    const handleNavClick = (menuKey) => {
        setMenu(menuKey);
        if (isMenuActive && toggleMenu) {
            toggleMenu();
        }
    };

    const navItems = [
        { key: 'dashboard', label: 'Overview', path: '/dashboard' },
        { key: 'sales', label: 'Sales Analytics', path: '/dashboard/sales-analytics' },
        { key: 'product-analytics', label: 'Product Analytics', path: '/dashboard/product-analytics' },
        { key: 'expenses', label: 'Expenses', path: '/dashboard/expense-management' },
        { key: 'website-analytics', label: 'Website Analytics', path: '/dashboard/website-analytics' },
    ];

    const managementItems = [
        { key: 'banners', label: 'Banners', path: '/dashboard/banners' },
        { key: 'order', label: 'Order List', path: '/dashboard/order_list' },
        { key: 'product', label: 'Products Catalog', path: '/dashboard/product' },
        { key: 'collection', label: 'Collections', path: '/dashboard/collection' },
        { key: 'gallery', label: 'Gallery', path: '/dashboard/gallery' },
        { key: 'customerGallery', label: 'Customer Gallery', path: '/dashboard/customerGallery' },
        { key: 'featured product', label: 'Featured Products', path: '/dashboard/featured_product' },
        { key: 'promo_code', label: 'Promo Codes', path: '/dashboard/promo_code' },
        { key: 'userList', label: 'Users', path: '/dashboard/userList' },
    ];

    const SidebarContent = ({ isMobile = false }) => (
        <div className="h-full flex flex-col min-h-screen">
            {/* Brand Logo & Header */}
            <div className="p-5 flex items-center justify-between border-b border-stone-100 flex-shrink-0">
                <Link to="/" className="flex items-center gap-3">
                    <img
                        src="../src/assets/images/logo.png"
                        alt="Logo"
                        className="h-9 w-auto object-contain"
                    />
                    <span className="font-bold text-base text-stone-900 tracking-tight">
                        ADMIN
                    </span>
                </Link>
                {isMobile && (
                    <button
                        onClick={toggleMenu}
                        className="p-1.5 text-stone-400 hover:text-stone-700 md:hidden"
                    >
                        ✕
                    </button>
                )}
            </div>

            {/* Admin Control Center Badge */}
            <div className="px-5 py-3 bg-stone-900 text-white flex items-center gap-2.5 text-xs font-semibold flex-shrink-0">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                <span>Admin Control Center</span>
            </div>

            {/* Nav Items List */}
            <nav className="p-3 space-y-6 overflow-y-auto flex-1">
                {/* Analytics Sub-Pages Section */}
                <div>
                    <p className="px-3 text-[10px] font-bold text-stone-400 uppercase tracking-widest mb-2">
                        Analytics & Intelligence
                    </p>
                    <ul className="space-y-1">
                        {navItems.map((item) => {
                            const isActive = menu === item.key;
                            return (
                                <li key={item.key}>
                                    <Link
                                        to={item.path}
                                        onClick={() => handleNavClick(item.key)}
                                        className={`flex items-center px-3.5 py-2.5 rounded-xl text-xs font-semibold transition-all duration-150 ${
                                            isActive
                                                ? 'bg-stone-900 text-white shadow-xs'
                                                : 'text-stone-600 hover:bg-stone-100/80 hover:text-stone-900'
                                        }`}
                                    >
                                        {item.label}
                                    </Link>
                                </li>
                            );
                        })}
                    </ul>
                </div>

                {/* Store Management Section */}
                <div>
                    <p className="px-3 text-[10px] font-bold text-stone-400 uppercase tracking-widest mb-2">
                        Store Management
                    </p>
                    <ul className="space-y-1">
                        {managementItems.map((item) => {
                            const isActive = menu === item.key;
                            return (
                                <li key={item.key}>
                                    <Link
                                        to={item.path}
                                        onClick={() => handleNavClick(item.key)}
                                        className={`flex items-center px-3.5 py-2.5 rounded-xl text-xs font-semibold transition-all duration-150 ${
                                            isActive
                                                ? 'bg-stone-900 text-white shadow-xs'
                                                : 'text-stone-600 hover:bg-stone-100/80 hover:text-stone-900'
                                        }`}
                                    >
                                        {item.label}
                                    </Link>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            </nav>
        </div>
    );

    return (
        <>
            {/* 1. Desktop Docked Sidebar (Always visible on >= 768px screens) */}
            <aside className="hidden md:flex w-64 flex-shrink-0 bg-white border-r border-stone-200/80 flex-col min-h-screen sticky top-0">
                <SidebarContent isMobile={false} />
            </aside>

            {/* 2. Mobile Slide-Over Drawer (< 768px screens) */}
            {isMenuActive && (
                <div
                    onClick={toggleMenu}
                    className="fixed inset-0 bg-stone-950/40 backdrop-blur-xs z-40 md:hidden transition-opacity"
                />
            )}
            <aside
                className={`fixed top-0 left-0 bottom-0 z-50 w-64 bg-white border-r border-stone-200/80 flex flex-col md:hidden transition-transform duration-300 ease-in-out ${
                    isMenuActive ? 'translate-x-0 shadow-2xl' : '-translate-x-full'
                }`}
            >
                <SidebarContent isMobile={true} />
            </aside>
        </>
    );
};

export default Navbar;